import React from 'react';
import { pricing, singleVisitPricing } from '../data/mock';
import { Check, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  return (
    <section id="pricing" className="section-padding bg-white">
      <div className="container">
        {/* Single Visit Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-sm uppercase tracking-widest text-[#b8860b] font-medium mb-4">
            Pricing
          </p>
          <h2 className="heading-1 text-[#0f172a] mb-4">
            Single Visit Consultations
          </h2>
          <p className="body-regular text-[#475569]">
            All prescription refills require a clinical consultation and are billed at the telemedicine rate (€150 established patients, €200 new patients).
          </p>
        </div>

        {/* Single Visit Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-20" data-testid="single-visit-grid">
          {singleVisitPricing.map((plan, index) => (
            <div
              key={index}
              data-testid={`single-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`relative border p-8 flex flex-col ${
                plan.popular
                  ? 'border-[#0f172a] border-2'
                  : 'border-[#e2e8f0]'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#b8860b] text-white text-xs font-medium px-4 py-1">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-[#0f172a] mb-1">{plan.name}</h3>
                <p className="text-sm text-[#64748b]">{plan.subtitle}</p>
              </div>
              <div className="text-center mb-6">
                <span className="text-4xl font-semibold text-[#0f172a]">{plan.price}</span>
                {plan.priceNote && <p className="text-xs text-[#64748b] mt-1">{plan.priceNote}</p>}
              </div>
              <p className="text-sm text-[#475569] text-center mb-6">{plan.description}</p>
              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#475569]">
                    <Check size={16} className="text-[#b8860b] flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link to="/book"
                className={`w-full py-3 text-center text-sm font-medium transition-colors block ${
                  plan.popular
                    ? 'bg-[#0f172a] text-white hover:bg-[#b8860b]'
                    : 'border border-[#0f172a] text-[#0f172a] hover:bg-[#0f172a] hover:text-white'
                }`}
                data-testid={`book-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {plan.name === 'Private Consultation' ? 'Book Home Visit' : 'Book Telemedicine'}
              </Link>
            </div>
          ))}
        </div>

        {/* After-hours WhatsApp notice */}
        <div className="max-w-4xl mx-auto mb-16 p-4 bg-[#f1f5f9] border border-[#e2e8f0] text-center">
          <p className="text-sm text-[#475569] mb-2">Need a visit outside these hours or at the weekend?</p>
          <a href="https://wa.me/34635794368" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#25d366] text-white text-sm font-medium hover:bg-[#1da851] transition-colors">
            <MessageCircle size={16} /> Contact us on WhatsApp
          </a>
        </div>

        {/* Membership Tiers Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-sm uppercase tracking-widest text-[#b8860b] font-medium mb-4">
            Membership
          </p>
          <h2 className="heading-1 text-[#0f172a] mb-4">
            Concierge Membership Tiers
          </h2>
          <p className="body-regular text-[#475569]">
            Ongoing, personalized medical care tailored to your needs.
          </p>
        </div>

        {/* Membership Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-testid="membership-grid">
          {pricing.map((plan, index) => (
            <div
              key={index}
              data-testid={`membership-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`relative border p-8 flex flex-col ${
                plan.popular
                  ? 'border-[#0f172a] border-2'
                  : 'border-[#e2e8f0]'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#b8860b] text-white text-xs font-medium px-4 py-1">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-[#0f172a] mb-1">{plan.name}</h3>
                <p className="text-sm text-[#64748b]">{plan.subtitle}</p>
              </div>
              {plan.price && (
                <div className="text-center mb-6">
                  <span className="text-4xl font-semibold text-[#0f172a]">{plan.price}</span>
                  {plan.period && (
                    <span className="text-base text-[#64748b]">{plan.period}</span>
                  )}
                </div>
              )}
              <p className="text-sm text-[#475569] text-center mb-6">{plan.description}</p>
              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#475569]">
                    <Check size={16} className="text-[#b8860b] flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className={`w-full py-3 text-center text-sm font-medium transition-colors block ${
                  plan.popular
                    ? 'bg-[#0f172a] text-white hover:bg-[#b8860b]'
                    : 'border border-[#0f172a] text-[#0f172a] hover:bg-[#0f172a] hover:text-white'
                }`}
              >
                Inquire Now
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
